import {cx} from './cx';

/**
 * The pokpok mark.
 *
 * ## The only image on this surface, and why it is allowed to be one
 *
 * Everything else here renders no picture at all — not the menu rows, not the
 * receipt, not the status list — because most V1 merchants have no photography
 * and because the perf budget is the binding constraint on a guest's own phone.
 * This is the exception, and it is a narrow one: ~9 KB, requested by the two
 * screens that are dead ends (Q19, Q20), which are the most exposed brand
 * surface the product has and the place a guest is already annoyed.
 *
 * It is **not** requested by the screens on the ordering path. A guest who is
 * choosing lunch does not need a logo, and `common.attribution` already says the
 * name in one line of body copy where it belongs.
 *
 * ## Not the wordmark, and not a lockup
 *
 * `pokpok-docs/design/brand-assets/svg/pokpok-wordmark-*.svg` is a different
 * asset and is still a **draft** — a `<text>` element with an open
 * "outline to paths once type is finalized" against it (`brand.html` §Wordmark).
 * An external SVG cannot see this page's `@font-face`, so shipping that one as
 * an image would render the brand's own name in whatever face the SVG renderer
 * picked. This mark has no type in it and no such problem.
 *
 * There is no endorsement lockup on this surface and the reserved slot is
 * released (D-014). This is the product's own mark, standing alone.
 *
 * ## Sizing
 *
 * `width`/`height` are set so the box is reserved before the bytes arrive — a
 * mark that pops in and shoves a headline down is worse than one that arrives a
 * beat late. The intrinsic file is 2× the largest drawn size; `height: auto` in
 * CSS keeps the ratio if a caller overrides the width.
 */
export function BrandMark({
  className,
  /** Drawn width in CSS px. The asset is 240×240 square, so 120 is a 2× fit. */
  width = 120,
}: {
  className?: string;
  width?: number;
}) {
  // Square: the mark is the mortar-and-pestle icon (owner, 2026-08-26), not the
  // old landscape pok/pok wordmark, so height tracks width one-to-one.
  const height = width;
  return (
    <img
      className={cx('pk-brand-mark', className)}
      src="/brand/pokpok-mark.png"
      width={width}
      height={height}
      // The product's name, once. The mark itself — a mortar and pestle in a
      // rounded red tile — is decoration and must not be described.
      alt="pokpokPOS"
      decoding="async"
    />
  );
}
